import * as React from 'react';
import {FC, RefObject, useCallback, useContext, useEffect, useState} from 'react';
import Chat, {ChatRef} from 'devextreme-react/chat';
import {Message, MessageEnteredEvent} from "devextreme/ui/chat";
import notify from "devextreme/ui/notify";
import {AttachmentTDO, CommentTDO} from "../../Models/ApiModels";
import styles from "./Service.module.scss";
import {IMAGE_TYPE, MAX_FILE_SIZE_BYTES, CHAT_MAX_LENGTH} from "../../AppConstants";
import {AppStoreContext} from "../../Stores/AppStore";
import {observer} from "mobx-react";
import clsx from "clsx";

type TicketChatProps = {
    comments: CommentTDO[];
    ticketId: string;
    disabled?: boolean;
    chatRef: RefObject<ChatRef>;
    saveComment: (e: MessageEnteredEvent) => void;
    addFile: (file: File) => void;
};
export const TicketChat: FC<TicketChatProps> = ({comments, chatRef, saveComment, addFile, ticketId, disabled}) => {
    const [messageLength, setMessageLength] = React.useState(0);

    const messageRender = useCallback(({message}) => {
        const comment = comments.find(e => e.id === message?.id);
        return (
            <>
                {
                    message?.text && <div className={message?.isCustomerBound ? styles.leftAlignment : styles.rightAlignment}>{message?.text}</div>
                }
                {
                    comment?.attachments && comment.attachments?.length > 0 && (
                        <div className={message?.isCustomerBound ? styles.leftAlignment : styles.rightAlignment}>
                            {comment?.attachments.map(a => {
                                return <Attachment key={a.id} attachment={a} commentId={comment.id} ticketId={ticketId}/>;
                            })}
                        </div>
                    )
                }
            </>
        )
    }, [comments, ticketId]);
    const onFileSelected = useCallback((e: any) => {
        const files: File[] = e.target.files;
        if (Boolean(files?.length)) {
            const filesArray = Array.from(files);

            const newFileList = filesArray.filter((f: File) => {
                return f.size <= MAX_FILE_SIZE_BYTES;
            });

            const oversized =  filesArray?.filter((f: File) => f.size > MAX_FILE_SIZE_BYTES);

            if (oversized.length > 0) {
                notify(
                    oversized.map((f: File) => `File "${f.name}" is too large. Maximum allowed size is 5 MB.`).join(", "),
                    "error",
                    3000
                );
            }

            addFile(newFileList[0]);
        }
    }, [addFile]);

    useEffect(() => {
        const chat = chatRef.current?.instance();
        if (chat) {
            const messageBoxNode = (chat as any)['_messageBox']['_$element'][0];
            const addFileBtn = document.createElement('label');
            addFileBtn.className = styles.attach;
            addFileBtn.innerHTML = `<i class="dx-icon dx-icon-attach"></i><input ${disabled ? 'disabled' : ''} id="dx-chat-attach" type="file">`;
            if (messageBoxNode.childNodes.length === 2) {
                messageBoxNode.appendChild(addFileBtn);
                const attach = document.getElementById('dx-chat-attach');
                attach?.addEventListener('change', onFileSelected);
            }
        }
    }, [onFileSelected, disabled, chatRef]);

    const handleContentReady = (e: any) => {
        const chatElement = e.element;
        requestAnimationFrame(() => {
            const textArea = chatElement.querySelector(".dx-texteditor-input");
            if (!textArea) return;
            textArea.maxLength = CHAT_MAX_LENGTH;
            textArea.addEventListener("input", (event: any) => {
                setMessageLength(event.target.value.length);
            });
        });
    };

    return (
        <div className={clsx(styles.chat, disabled && styles.chat_disabled)}>
            <Chat
                ref={chatRef}
                height="calc(100vh - 205px)"
                items={comments.map(c => {
                    return {
                        id: c.id,
                        text: c.content,
                        timestamp: c.modifiedOn,
                        isCustomerBound: c.isCustomerBound,
                        author: {
                            name: c.user.displayName
                        }
                    } as Message;
                })}
                showDayHeaders={true}
                showMessageTimestamp={true}
                showAvatar={false}
                showUserName={true}
                messageRender={messageRender}
                onMessageEntered={saveComment}
                onContentReady={handleContentReady}
            />
            <div className={styles.counter}>
                {messageLength}/{CHAT_MAX_LENGTH}
            </div>
        </div>
    );
};
type AttachmentProps = {
    attachment: AttachmentTDO
    commentId: string,
    ticketId: string
}
const Attachment: FC<AttachmentProps> = observer(({attachment}) => {
    const context = useContext(AppStoreContext);
    const [src, setSrc] = useState<string>('');
    const {attachmentKeys, attachmentsData} = context;

    useEffect(() => {
        const data = attachmentsData.get(attachment.id);
        if (attachmentKeys.includes(attachment.id)&& data && data !== src) {
            setSrc(data);
        }
    }, [attachmentKeys, attachmentsData, attachment.id, src]);

    const _onClick = useCallback(async () => {
        const link = document.createElement('a');
        link.href = src;
        link.setAttribute('download', attachment.name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(src), 0);
        return false;
    }, [attachment, src]);
    return (
        <div>
            <span className={styles.link} title={src} onClick={_onClick}>
                {
                    IMAGE_TYPE.find(e => attachment.name.endsWith(e)) ?
                        src ? <img src={src} className={styles.prev} alt={attachment.name}/> : <div className={styles.loader}/>
                        : attachment.name
                }
            </span>
        </div>
    );
});